import { authMethodsSchema, type AuthMethods, type SessionUser } from '@lms/shared-types'
import { apiRequest } from '../api/client'

/**
 * Web → API auth client.
 *
 * Targets the FastAPI auth router under `/api/v1/auth/*`. The dev vite proxy
 * forwards `/api/*` to the api container; in prod the same relative path
 * resolves against the deployed origin. Either way the request is same-origin
 * from the browser's perspective so the httpOnly session cookie sticks
 * without CORS gymnastics.
 *
 * The api wraps responses in `{ success, data }`; `apiRequest` unwraps that
 * envelope, so these helpers return the data shape directly and throw
 * `ApiError` on non-2xx.
 */

export async function signInWithPassword(
  email: string,
  password: string,
): Promise<SessionUser> {
  return apiRequest<SessionUser>('/api/v1/auth/login', {
    method: 'POST',
    body: { email, password },
  })
}

export async function signOutSession(): Promise<void> {
  await apiRequest<unknown>('/api/v1/auth/logout', { method: 'POST' })
}

export async function fetchSessionUser(): Promise<SessionUser> {
  return apiRequest<SessionUser>('/api/v1/auth/me')
}

/** Which sign-in methods the api accepts here (public, no session needed). */
export async function fetchAuthMethods(): Promise<AuthMethods> {
  const raw = await apiRequest<unknown>('/api/v1/auth/methods')
  return authMethodsSchema.parse(raw)
}

/**
 * Microsoft Entra OIDC is a server-side flow: the api owns the redirect to
 * Entra and the callback that exchanges the code for a session cookie. The
 * browser just navigates to the login URL.
 *
 * Prefixed with the API base (same as `apiRequest`) so it works under a
 * static web build that doesn't proxy `/api` (k8s nginx): when
 * `VITE_API_URL` is set the nav targets the api directly; when empty (prod
 * same-origin) it stays relative — unchanged behaviour there.
 */
const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '')
export const MICROSOFT_LOGIN_PATH = `${API_BASE}/api/v1/auth/login/microsoft`
